import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { RootState } from '../../store'
import { showBanner } from '../../global-state/banner-slice'
import { webinarServices } from '../../services/webinar-service'
import { YoutubeEmbedModal } from '../../lib/components/YoutubeEmbedModal'
import { WebinarCard, WebinarCardData } from './components/webinar-card'
import { shareWebinarLink, WEBINAR_SHARE_QUERY_PARAM } from './webinar-share'
import './webinars.scss'

const DEFAULT_LIMIT = 10

const findWebinarMatch = (
  webinars: WebinarCardData[],
  {
    slug,
    webinarId,
  }: {
    slug?: string | null
    webinarId?: number | null
  },
) => {
  if (slug) {
    const slugMatch = webinars.find((webinar) => webinar.slug === slug)
    if (slugMatch) return slugMatch
  }

  if (webinarId !== null && webinarId !== undefined && Number.isFinite(webinarId)) {
    return webinars.find((webinar) => Number(webinar.id) === webinarId)
  }

  return undefined
}

export const WebinarsPage = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { slug: slugParam } = useParams()
  const [searchParams] = useSearchParams()
  const { authToken } = useSelector((state: RootState) => state.investor)
  const [selectedWebinar, setSelectedWebinar] = useState<WebinarCardData | null>(null)
  const [pagination, setPagination] = useState({
    total: 0,
    limit: DEFAULT_LIMIT,
    count: 0,
    offset: 0,
  })
  const [accumulatedWebinars, setAccumulatedWebinars] = useState<WebinarCardData[]>([])
  const deepLinkResolvedRef = useRef<string | null>(null)

  const { data, isFetching } = useQuery({
    queryKey: ['webinars-v1', pagination.offset],
    queryFn: async () => {
      return webinarServices.getWebinars(
        {
          limit: pagination.limit.toString(),
          offset: pagination.offset.toString(),
        },
        {
          Authorization: authToken ?? '',
        },
      )
    },
  })

  useEffect(() => {
    if (!data?.webinars) return
    if (data.pagination) setPagination(data.pagination)
    const pageOffset = data.pagination?.offset ?? 0
    const webinars = data.webinars ?? []
    if (pageOffset === 0) {
      setAccumulatedWebinars(webinars)
    } else {
      setAccumulatedWebinars((prev) => {
        const seenIds = new Set(prev.map((webinar) => webinar.id))
        const unique = webinars.filter((webinar: WebinarCardData) => !seenIds.has(webinar.id))
        return [...prev, ...unique]
      })
    }
  }, [data])

  const openWebinar = useCallback(
    (webinar: WebinarCardData) => {
      setSelectedWebinar(webinar)
      navigate(`/webinars/${encodeURIComponent(webinar.slug)}`, { replace: true })
    },
    [navigate],
  )

  const handleCloseModal = useCallback(() => {
    setSelectedWebinar(null)
    if (slugParam || searchParams.has(WEBINAR_SHARE_QUERY_PARAM)) {
      navigate('/webinars', { replace: true })
    }
  }, [navigate, searchParams, slugParam])

  const handleShareWebinar = useCallback(
    async (webinar: WebinarCardData) => {
      const result = await shareWebinarLink(webinar)
      if (result.cancelled) return

      dispatch(
        showBanner({
          text: result.ok
            ? result.method === 'clipboard'
              ? 'Webinar link copied to clipboard'
              : 'Webinar link shared'
            : 'Failed to share webinar link',
          variant: result.ok ? 'success-banner' : 'error-banner',
        }),
      )
    },
    [dispatch],
  )

  useEffect(() => {
    const legacyWebinarIdParam = searchParams.get(WEBINAR_SHARE_QUERY_PARAM)
    const legacyWebinarId = legacyWebinarIdParam ? Number(legacyWebinarIdParam) : NaN
    const deepLinkKey = slugParam
      ? `slug:${slugParam}`
      : legacyWebinarIdParam
      ? `id:${legacyWebinarIdParam}`
      : null

    if (!deepLinkKey) {
      deepLinkResolvedRef.current = null
      return
    }

    if (legacyWebinarIdParam && !Number.isFinite(legacyWebinarId)) return

    const targetWebinarId = Number.isFinite(legacyWebinarId) ? legacyWebinarId : null

    if (
      selectedWebinar &&
      ((slugParam && selectedWebinar.slug === slugParam) ||
        (targetWebinarId !== null && Number(selectedWebinar.id) === targetWebinarId))
    ) {
      return
    }

    if (isFetching || !data?.webinars) return

    const webinarsToSearch = accumulatedWebinars.length > 0 ? accumulatedWebinars : data.webinars
    const match = findWebinarMatch(webinarsToSearch, {
      slug: slugParam ?? null,
      webinarId: targetWebinarId,
    })

    if (match) {
      setSelectedWebinar(match)
      deepLinkResolvedRef.current = deepLinkKey
      return
    }

    if (deepLinkResolvedRef.current === deepLinkKey) return

    const total = data.pagination?.total ?? pagination.total
    if (accumulatedWebinars.length < total) {
      setPagination((prev) => ({
        ...prev,
        offset: prev.offset + DEFAULT_LIMIT,
      }))
      return
    }

    deepLinkResolvedRef.current = deepLinkKey
  }, [
    accumulatedWebinars,
    data,
    isFetching,
    pagination.total,
    searchParams,
    selectedWebinar,
    slugParam,
  ])

  const hasMore = pagination.offset + pagination.limit < pagination.total

  const handleLoadMore = useCallback(() => {
    setPagination((prev) => ({
      ...prev,
      offset: prev.offset + DEFAULT_LIMIT,
    }))
  }, [])

  return (
    <div className='webinars-page'>
      <section className='webinars-hero-section' aria-label='Webinars'>
        <div className='webinars-hero-content'>
          <h1 className='webinars-hero-title'>Webinars</h1>
          <p className='webinars-hero-subtitle'>
            On-demand sessions and expert conversations on private markets
          </p>
        </div>
      </section>

      <section className='webinars-body'>
        {isFetching && accumulatedWebinars.length === 0 && (
          <div className='webinars-loading'>
            <div className='webinars-loading__spinner' />
          </div>
        )}

        {accumulatedWebinars.length === 0 && !isFetching && (
          <p className='webinars-empty'>No webinars available at the moment.</p>
        )}

        {accumulatedWebinars.length > 0 && (
          <div className='webinars-grid'>
            {accumulatedWebinars.map((webinar) => (
              <WebinarCard
                key={webinar.id}
                webinar={webinar}
                onClick={openWebinar}
                onShare={handleShareWebinar}
              />
            ))}
          </div>
        )}

        {hasMore && !isFetching && (
          <div className='webinars-load-more-wrapper'>
            <button className='webinars-load-more-btn' onClick={handleLoadMore} type='button'>
              Load more webinars
            </button>
          </div>
        )}
      </section>

      <YoutubeEmbedModal
        videoId={selectedWebinar?.youtube_video_id ?? ''}
        isOpen={Boolean(selectedWebinar)}
        onClose={handleCloseModal}
        title={selectedWebinar?.title ?? 'YouTube video player'}
        testId='webinar-youtube-modal'
        onShare={selectedWebinar ? () => handleShareWebinar(selectedWebinar) : undefined}
      />
    </div>
  )
}
